import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, FileText, FileSignature, Truck, ListChecks, Inbox, AlertTriangle, RefreshCcw } from "lucide-react";
import StatusBadge from "../components/StatusBadge";

function Stat({ icon: Icon, label, value, hint, testid }) {
    return (
        <div className="sc-card p-4" data-testid={testid}>
            <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-xs uppercase tracking-wider">{label}</span>
                <Icon className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="mt-2 text-3xl font-semibold tracking-tight font-mono tabular-nums">{value ?? "—"}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

export default function Dashboard() {
    const [s, setS] = useState(null);
    const [health, setHealth] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c] = await Promise.all([
                    api.get("/dashboard/summary"),
                    api.get("/sap/health"),
                    api.get("/quotations"),
                ]);
                setS(a.data); setHealth(b.data); setRecent(c.data.slice(0, 8));
            } catch (e) { /* noop */ }
        })();
    }, []);

    return (
        <div className="p-6 space-y-6" data-testid="dashboard-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="sc-section-title">Overview</div>
                    <h1 className="text-2xl font-semibold tracking-tight">Sale Hub Operations</h1>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Activity className="w-4 h-4" />
                    HANA: <StatusBadge status={health?.hana?.ok ? "active" : "failed"} />
                    Service Layer: <StatusBadge status={health?.service_layer?.ok ? "active" : "failed"} />
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Inbox} label="Pending Approvals" value={s?.pending_approvals} testid="kpi-pending-approvals" />
                <Stat icon={FileText} label="Quotations" value={s?.quotations_count} testid="kpi-quotations" />
                <Stat icon={FileSignature} label="Sales Orders" value={s?.sales_orders_count} testid="kpi-sales-orders" />
                <Stat icon={Truck} label="Deliveries Today" value={s?.deliveries_today} testid="kpi-deliveries" />
                <Stat icon={ListChecks} label="SAP Posting Queue" value={s?.posting_queue} testid="kpi-posting-queue" />
                <Stat icon={AlertTriangle} label="SAP Posting Failed" value={s?.posting_failed} testid="kpi-posting-failed" />
                <Stat icon={RefreshCcw} label="Last Sync" value={s?.last_sync ? s.last_sync.kind : "—"} hint={s?.last_sync ? new Date(s.last_sync.started_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "No sync yet"} testid="kpi-last-sync" />
                <Stat icon={Activity} label="SAP Mode" value={s?.sap_mode || "MOCK"} hint="Mock middleware" testid="kpi-sap-mode" />
            </div>

            <div className="sc-card">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h2 className="text-sm font-medium tracking-tight">Recent quotations</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full sc-table">
                        <thead>
                            <tr>
                                <th>Doc no</th>
                                <th>Customer</th>
                                <th>Created by</th>
                                <th>Status</th>
                                <th>SAP</th>
                                <th className="text-right">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No quotations yet.</td></tr>
                            )}
                            {recent.map((q) => (
                                <tr key={q.id} data-testid={`recent-quotation-${q.id}`}>
                                    <td className="font-mono">{q.doc_no}</td>
                                    <td>{q.customer_snapshot?.company_name || q.end_customer_name || q.customer_name}</td>
                                    <td className="text-muted-foreground">{q.user_email}</td>
                                    <td><StatusBadge status={q.status} /></td>
                                    <td><StatusBadge status={q.sap_status} /></td>
                                    <td className="sc-num">{Number(q.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
